import { useRef, type ReactNode, type RefObject } from "react";
import { useFocusTrap } from "./useFocusTrap";

export interface ModalProps {
  /** `alertdialog` for a confirmation the user must answer; `dialog` for a form (default). */
  role?: "dialog" | "alertdialog";
  /** Id of the element naming the dialog — its heading. Every dialog must name itself. */
  labelledBy: string;
  /** Id of the element describing it (the body copy of a confirmation). */
  describedBy?: string;
  /** Extra class(es) on the dialog box, alongside the shared `modal`. */
  className?: string;
  /** `data-testid` on the dialog box — what the E2E suite locates. */
  testId?: string;
  /** Dismiss without committing: bound to Escape. Omitted → Escape does nothing (a dialog with no way out). */
  onClose?: () => void;
  /** Suspend Escape while a write is in flight, so a keystroke cannot take the dialog away mid-request. */
  closeDisabled?: boolean;
  /**
   * Also dismiss on a press AND release on the backdrop (never on the dialog itself, and never on a drag
   * that merely ends out there). Opt-in, and only ever an ADDITION
   * to Escape and a cancel button: the pointer gesture a "click outside to dismiss" reader expects, on a
   * surface that stays `role="presentation"` so it is not announced and is not a tab stop. A confirmation
   * that must be answered (`alertdialog`) should leave it off — an accidental click is not an answer.
   */
  closeOnBackdrop?: boolean;
  /** First field of a form dialog. Omitted → focus lands on the dialog itself, at its heading. */
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * THE dialog primitive: every `aria-modal` surface in the SPA is this component. Nothing else in
 * `apps/web/src` may write `aria-modal` / `role="dialog"` / `role="alertdialog"` by hand — the guardrail in
 * `src/ui/modal-primitive.test.mjs` (run by `make frontend-test`) fails the build if it does.
 *
 * Why one primitive rather than six hand-rolled dialogs: `aria-modal="true"` tells assistive technology the
 * page behind is inert, and until this existed nothing in the app made that true for the keyboard — Tab
 * walked straight out of every dialog into the page behind it (WCAG 2.1 AA 2.4.3, and the intent of 2.1.2).
 * Six hand-rolled traps is how the seventh dialog ships without one.
 *
 * What it guarantees, via `useFocusTrap`:
 *   - focus moves into the dialog on open (its first field, or the dialog itself);
 *   - Tab from the last control cycles to the first and Shift+Tab from the first to the last — focus never
 *     reaches the page behind;
 *   - Escape dismisses (unless `closeDisabled`);
 *   - on close the keyboard goes back to the control that opened it, unless the caller has already handed it
 *     somewhere deliberate.
 *
 * The backdrop stays presentational: dismissal is always Escape or a button, both keyboard-operable, so the
 * dialog is never dependent on a pointer. `closeOnBackdrop` adds the click-outside gesture on top of those
 * two for the dialogs where it is the expected shape — it is an extra route out, never the only one. A press
 * on the backdrop never moves focus either way (see `onMouseDown`), so a stray click beside a dialog cannot
 * cost the keyboard its way back to the opener.
 */
export function Modal({
  role = "dialog",
  labelledBy,
  describedBy,
  className,
  testId,
  onClose,
  closeDisabled = false,
  closeOnBackdrop = false,
  initialFocus,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Did the CURRENT gesture start on the backdrop? A `click` does not fire on the element that was
  // pressed: when mousedown and mouseup land on different elements it fires on their nearest common
  // ancestor — which, for a press inside the dialog released over the backdrop, IS the backdrop. So
  // `e.target === e.currentTarget` on click alone would dismiss a drag that merely ENDED outside
  // (selecting text in a field, a sloppy click near the edge), throwing away whatever was typed.
  const pressedBackdrop = useRef(false);

  useFocusTrap(dialogRef, {
    onEscape: onClose,
    escapeEnabled: !closeDisabled,
    initialFocus: initialFocus ?? "container",
  });

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        const onBackdrop = e.target === e.currentTarget;
        pressedBackdrop.current = onBackdrop;
        // A press on the backdrop must not move focus. The dialog renders in place, so the browser would
        // hand focus to the nearest focusable ancestor (a tab panel, say) — and the focus trap reads focus
        // sitting outside the dialog as deliberate and skips returning it to the opener.
        //
        // THIS RUNS FOR EVERY MODAL, not only the ones that dismiss on a backdrop click, and the dialogs
        // that do NOT dismiss are the ones that need it most: the click leaves them standing (which is the
        // point of leaving `closeOnBackdrop` off), so the keyboard is still owed its way back — and a
        // consultant who clicks beside a confirmation and then presses Escape would otherwise land at the
        // top of the document instead of on the control that asked the question. The backdrop is
        // `role="presentation"` and was never a focus target in the first place.
        if (onBackdrop) e.preventDefault();
      }}
      onClick={
        closeOnBackdrop && onClose && !closeDisabled
          ? (e) => {
              // Both ends of the gesture on the backdrop itself — a press that started inside the
              // dialog must never dismiss it, however it ended.
              const startedOnBackdrop = pressedBackdrop.current;
              // Clear it either way, so a stale `true` cannot survive into a later gesture.
              pressedBackdrop.current = false;
              if (startedOnBackdrop && e.target === e.currentTarget) onClose();
            }
          : undefined
      }
    >
      <div
        className={className ? `modal ${className}` : "modal"}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        ref={dialogRef}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}
